import './TeamMembersTable.scss';

import * as React from 'react';
import { ForwardedRef } from 'react';

import {
  ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import cx from 'classnames';
import { atom, useAtom, useAtomValue } from 'jotai';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { translate } from '~l10n/client';
import { IconButton, SmallButton } from '~shared/components/buttons';
import { FocusIndicator } from '~shared/components/FocusIndicator';
import { IconChevron } from '~shared/components/icons/standard';
import { Sort } from '~shared/components/Table/update/components/Sort';
import TextInput from '~shared/components/TextInput';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { InviteTeamMembersDialog } from '~teacher/components/Account/TeamMembers/InviteTeamMembersDialog';
import { RemoveFromTeamDialog } from '~teacher/components/Account/TeamMembers/RemoveFromTeamDialog';
import { SeatsAvailableDialog } from '~teacher/components/Account/TeamMembers/SeatsAvailableDialog';
import {
  ChangeToPaidAddSeatsDialog,
  OverPurchasedSeatsDialog,
} from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog';
import { InviteOverPlanSeatLimitDialog } from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog/InviteOverPlanSeatLimitDialog';
import { EllipsisMenu } from '~teacher/components/Account/TeamMembers/TeamMembersTable/EllipsisMenu';
import { NameColumn } from '~teacher/components/Account/TeamMembers/TeamMembersTable/NameColumn';
import { RoleActionMenu } from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu';
import { SeatAction } from '~teacher/components/Account/TeamMembers/TeamMembersTable/SeatAction';
import { UpgradeMemberDialog } from '~teacher/components/Account/TeamMembers/TeamMembersTable/UpgradeMemberDialog';
import { TransferTeamOwnershipDialog } from '~teacher/components/Account/TeamMembers/TransferTeamOwnershipDialog';
import { useTeamsMembers } from '~teacher/hook/team';

import {
  TeamMember,
  TeamsMembersData,
} from '../../../../../api/teams/getTeamsMembers';

const columnHelper = createColumnHelper<TeamMember>();

interface SortableHeaderProps {
  children: React.ReactNode;
  toggleSort: ((event: unknown) => void) | undefined;
  sortEnabled: boolean;
  descending: boolean;
}

function SortableHeader({
  children,
  toggleSort,
  sortEnabled,
  descending,
}: SortableHeaderProps) {
  return (
    <button
      className="group relative bg-transparent border-transparent grid border-0 rounded-2 text-xs font-700 leading-8 text-cyan-700 tracking-wider uppercase cursor-pointer focus:outline-none"
      style={{ gridTemplateColumns: '1fr auto' }}
      onClick={toggleSort}
    >
      {children}
      <Sort enabled={sortEnabled} descending={descending} />
      <FocusIndicator />
    </button>
  );
}

const teamsColumns = [
  columnHelper.accessor(({ name }) => name, {
    id: 'name',
    cell: (props) => (
      <NameColumn
        name={props.getValue()}
        email={props.row.original.email}
        publicId={props.row.original.publicId}
      />
    ),
    header: (props) => (
      <SortableHeader
        sortEnabled={props.column.getCanSort() && !!props.column.getIsSorted()}
        toggleSort={props.column.getToggleSortingHandler()}
        descending={props.column.getIsSorted() === 'desc'}
      >
        {translate('Name')}
      </SortableHeader>
    ),
    sortingFn: 'textCaseSensitive',
  }),
  columnHelper.accessor(({ email }) => email, {
    id: 'email',
    enableHiding: true,
  }),
  columnHelper.accessor(({ role }) => role, {
    id: 'role',
    cell: (props) => (
      <RoleActionMenu
        targetPublicId={props.row.original.publicId}
        targetMemberRole={props.getValue()}
      />
    ),
    header: (props) => (
      <SortableHeader
        sortEnabled={props.column.getCanSort() && !!props.column.getIsSorted()}
        toggleSort={props.column.getToggleSortingHandler()}
        descending={props.column.getIsSorted() === 'desc'}
      >
        {translate('Role')}
      </SortableHeader>
    ),
    sortingFn: 'basic',
  }),
  columnHelper.accessor(({ accessLevel }) => accessLevel, {
    id: 'access',
    cell: (props) => (
      <SeatAction
        publicId={props.row.original.publicId}
        accessLevel={props.getValue()}
      />
    ),
    header: (props) => (
      <SortableHeader
        sortEnabled={props.column.getCanSort() && !!props.column.getIsSorted()}
        toggleSort={props.column.getToggleSortingHandler()}
        descending={props.column.getIsSorted() === 'desc'}
      >
        {translate('Seat')}
      </SortableHeader>
    ),
    sortingFn: 'basic',
  }),
  columnHelper.display({
    id: 'actions',
    cell: (props) => {
      const { publicId, role } = props.row.original;
      return <EllipsisMenu publicId={publicId} role={role} />;
    },
  }),
];

interface TeamMembersCellProps {
  children: React.ReactNode;
  column: ColumnDef<TeamsMembersData[number]>;
  onClick?: (event: unknown) => void;
  className?: string;
  isSelected?: boolean;
  ariaSort?: 'none' | 'ascending' | 'descending' | 'other';
  ariaRole: 'gridcell' | 'columnheader';
}

const TeamMembersCell = React.forwardRef(function TeamMembersCell(
  {
    children,
    column,
    onClick,
    isSelected,
    className,
    ariaSort,
    ariaRole,
  }: TeamMembersCellProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const cellCx = cx(
    'flex items-center antialiased team-member-cell ',
    'text-left',
    {
      ['flex-grow flex-1 truncate']:
        column.id === 'name' && ariaRole === 'gridcell',
      ['justify-start flex-shrink flex-1']:
        column.id === 'access' || column.id === 'role',
      ['flex items-center justify-center min-w-16']: column.id === 'actions',
      ['bg-cyan-50']: isSelected,
      [className as string]: !!className,
    }
  );
  return (
    <div
      aria-sort={ariaSort}
      role={ariaRole}
      ref={ref}
      className={cellCx}
      onClick={onClick}
    >
      {children}
    </div>
  );
});

const initialValue = '';
const globalTableFilterAtom = atom<string>(initialValue);
globalTableFilterAtom.onMount = (setAtom) => {
  return () => {
    // This runs when atom is unmounted
    setAtom(initialValue);
  };
};

export function TeamMembersTable() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);

  const teamMembersQuery = useTeamsMembers({
    teamId: selectedTeamId,
  });

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [globalFilter] = useAtom(globalTableFilterAtom);

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    columns: teamsColumns,
    data: teamMembersQuery.data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: 'includesString',
    enableSorting: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    initialState: {
      columnVisibility: {
        email: false,
      },
    },
    state: {
      pagination,
      sorting,
      globalFilter,
    },
  });

  return (
    <>
      <div
        role="grid"
        className="team-members-table col-span-2 w-full relative"
      >
        {/* header row */}
        <div role="row" className="team-members-table-row">
          {table.getHeaderGroups().map((headerGroup) => {
            return headerGroup.headers.map((header) => {
              let ariaSort: 'ascending' | 'descending';

              if (header.column.getIsSorted() === 'asc') {
                ariaSort = 'ascending';
              } else if (header.column.getIsSorted() === 'desc') {
                ariaSort = 'descending';
              }

              return (
                <TeamMembersCell
                  key={header.id}
                  column={header.column}
                  ariaSort={ariaSort}
                  ariaRole="columnheader"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TeamMembersCell>
              );
            });
          })}
        </div>
        {/* body rows */}
        {table.getRowModel().rows.map((row) => {
          const isSelected = row.getIsSelected();
          return (
            <div
              role="row"
              className={cx('team-members-table-row', {
                ['bg-cyan-50']: isSelected,
              })}
              key={row.id}
            >
              {row.getVisibleCells().map((cell) => (
                <TeamMembersCell
                  className="font-sans text-base text-blue-900"
                  ariaRole="gridcell"
                  key={cell.id}
                  column={cell.column}
                >
                  <div className="pl-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </TeamMembersCell>
              ))}
            </div>
          );
        })}
      </div>
      {table.getPageCount() > 1 ? (
        <div className="flex items-center justify-center gap-4 font-sans text-base">
          <IconButton
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-disabled={!table.getCanPreviousPage()}
          >
            <IconChevron direction="left" />
            <span className="sr-only">{translate('Previous')}</span>
          </IconButton>
          <span className="flex items-center gap-1">
            {translate('{0} of {1}').format(
              table.getState().pagination.pageIndex + 1,
              table.getPageCount().toLocaleString()
            )}
          </span>
          <IconButton
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-disabled={!table.getCanNextPage()}
          >
            <IconChevron direction="right" />
            <span className="sr-only">{translate('Next')}</span>
          </IconButton>
        </div>
      ) : null}
    </>
  );
}

interface TeamMembersTableHeaderProps {
  canInvite?: boolean;
}

export function TeamMembersTableHeader({
  canInvite = false,
}: TeamMembersTableHeaderProps) {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);

  const [isOpen, setIsOpen] = React.useState(false);
  const [, setGlobalFilter] = useAtom(globalTableFilterAtom);

  return (
    <>
      <div className="col-span-2 gap-2 grid grid-cols-subgrid">
        <div className="flex flex-col gap-4">
          <span className="text-display font-display font-700 text-display leading-8">
            {translate('Team Members')}
          </span>
          <span className="font-sans text-base font-400 leading-8">
            {translate(
              'Share a plan with others. Users can only access their own content.'
            )}
          </span>
        </div>
        <div className="grid auto-rows-min content-end  items-end gap-2">
          {canInvite ? (
            <SmallButton
              className="whitespace-no-wrap w-70 md:w-full"
              appearance="primary"
              onClick={() => {
                new FeatureUseEventAdapter({
                  feature: 'invite_members_button',
                  action: 'click',
                }).sendAnalytics();
                setIsOpen(true);
              }}
            >
              {translate('Invite Members')}
            </SmallButton>
          ) : null}
        </div>
      </div>
      <div className="col-span-2 flex gap-4">
        <TextInput
          placeholder={translate('Search by name or email')}
          onChange={(event) => {
            setGlobalFilter(event.target.value.trim());
          }}
        />
      </div>
      <InviteTeamMembersDialog
        teamId={selectedTeamId}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
      <OverPurchasedSeatsDialog />
      <ChangeToPaidAddSeatsDialog />
      <UpgradeMemberDialog />
      <RemoveFromTeamDialog />
      <TransferTeamOwnershipDialog />
      <SeatsAvailableDialog />
      <InviteOverPlanSeatLimitDialog />
    </>
  );
}
